import { Box, Button, Checkbox, CheckboxGroup, Flex, HStack, SimpleGrid, Text, useToast, VStack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'
import { Input } from '../../../../../components/Form/Input'
import { useAuth } from '../../../../../context/AuthContext'
import { Step } from '../../../../../Enuns/step.enum'
import { WorkSchedule } from '../../../../../Interfaces/workSchedule'
import { WorkScheduleEmployee } from '../../../../../Interfaces/workScheduleEmployee'
import { api } from '../../../../../services/api'

// Validação do esquema com Yup
const createServiceOrderSchema = yup.object().shape({
  work_date: yup.string().required('Informe a data'),
  start_work: yup.string().required('Informe a hora de início'),
  end_work: yup.string().required('Informe a hora de término'),
  // Removido employee_ids da validação pois não está sendo usado no formulário
})

interface Props {
  selectedProduct?: WorkSchedule
  orderId: string
  onClose: () => void
  step: Step
}

export default function WorkScheduleCreateOrUpdate({ selectedProduct, onClose, orderId, step }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const id = selectedProduct?.id ?? ''

  const { register, handleSubmit, formState, reset, getValues } = useForm({
    resolver: yupResolver(createServiceOrderSchema),
    mode: 'onSubmit',
  })

  const errors = formState.errors
  const [selectedEmployees, setSelectedEmployees] = useState<WorkScheduleEmployee[]>([])
  const [hourlyValue, setHourlyValue] = useState(0)
  const [totalValue, setTotalValue] = useState(0)
  const [hasStartTime, setHasStartTime] = useState(false)
  const [hasEndTime, setHasEndTime] = useState(false)
  const { hasPermission } = useAuth()
  const canViewFinancialData = hasPermission('view_financial_data_on_demand')
  // Requisição para buscar os funcionários
  const { data: employees } = useQuery(
    'employees',
    async () => {
      const response = await api.get('/employees')
      return response.data
    },
    {
      retry: false,
    },
  )

  const isCreateMode = !id

  const createDemand = useMutation(
    async (product: WorkSchedule) => {
      const dateParts = product.work_date.split('-') // Separa a data em [ano, mês, dia]
      const dateLocal = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]))

      const workSchedule = {
        ...product,
        work_date: format(dateLocal, 'yyyy-MM-dd'),
        step,
        employees: selectedEmployees, // Apenas IDs
        hourly_value: hourlyValue, // Envia o valor total calculado
        total_value: totalValue, // Envia o valor total calculado
      }

      const response = await api.post(`/service-orders/${orderId}/work-schedules`, workSchedule)
      
      if (response) {
        toast({ description: 'Cadastrado com sucesso.' })
        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('workSchedule')
        queryClient.refetchQueries()
      },
      onError: (error: any) => {
        const errorList = error?.response?.data?.errors

        if (errorList && Array.isArray(errorList)) {
          const toastOptions = errorList.map((err: any) => ({ description: err.message, status: 'error' as const }))
          toastOptions.forEach((t) => toast(t))
        } else {
          toast({ description: 'Falha ao cadastrar', status: 'error' })
        }
      },
    },
  )

  useEffect(() => {
    if (id && selectedProduct) {
      reset({
        ...selectedProduct,
        work_date: selectedProduct.work_date
          ? format(new Date(`${selectedProduct.work_date}T00:00:00`), 'dd/MM/yyyy')
          : '',
      })
      
      // Garantir que os funcionários sejam carregados corretamente
      const employees = selectedProduct.employees || []
      setSelectedEmployees(employees)
      setHasStartTime(!!selectedProduct.start_work)
      setHasEndTime(!!selectedProduct.end_work)
      
      if (selectedProduct && employees.length > 0) {
        calculateTotalValue(selectedProduct)
      }
    } else if (!id) {
      // Se for um novo registro, definir a data atual
      const today = format(new Date(), 'dd/MM/yyyy')
      reset({
        work_date: today,
        start_work: '',
        end_work: '',
      })
      setSelectedEmployees([])
      setHasStartTime(false)
      setHasEndTime(false)
    }
  }, [id, selectedProduct, reset])

  const updateDemands = useMutation(
    async (product: WorkSchedule) => {
      const dateParts = product.work_date.split('-') // Separa a data em [ano, mês, dia]
      const dateLocal = new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]))
      const workSchedule = {
        ...product,
        work_date: format(dateLocal, 'yyyy-MM-dd'),
        step,
        employees: selectedEmployees, // Apenas IDs
        hourly_value: hourlyValue,
        total_value: totalValue,
      }

      const response = await api.put(`/service-orders/${orderId}/work-schedules/${id}`, workSchedule)
      if (response) {
        toast({ description: 'Atualizado com sucesso.' })
        onClose()
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('workSchedule')
        queryClient.refetchQueries()
      },
      onError: ({ response }) => {
        const errorList = response?.data.errors

        if (errorList) {
          const toastOptions = errorList.map((err) => ({ description: err.message, status: 'error' }))
          toastOptions.forEach((t) => toast(t))
        } else {
          toast({ description: 'Falha ao atualizar', status: 'error' })
        }
      },
    },
  )

  const handleCreateOrUpdate: SubmitHandler<WorkSchedule> = async (values) => {
    // Verificar se há erros de validação
    if (Object.keys(formState.errors).length > 0) {
      return
    }
    
    // Validação customizada para funcionários
    if (selectedEmployees.length === 0) {
      toast({ description: 'Selecione pelo menos um funcionário', status: 'error' })
      return
    }
    
    // Formatar a data corretamente
    const dateFormatted = format(new Date(`${values.work_date.split('/').reverse().join('-')}T00:00:00`), 'yyyy-MM-dd')
    
    // Formatar as horas corretamente
    const startWorkFormatted = values.start_work ? (values.start_work.split(':').length === 2 ? `${values.start_work}:00` : values.start_work) : null
    const endWorkFormatted = values.end_work ? (values.end_work.split(':').length === 2 ? `${values.end_work}:00` : values.end_work) : null
    
    const formattedData = {
      ...values,
      employees: selectedEmployees,
      work_date: dateFormatted,
      start_work: startWorkFormatted,
      end_work: endWorkFormatted,
    }
    
    // Remover campos que não devem ser enviados
    if ('employee_ids' in formattedData) {
      delete (formattedData as any).employee_ids
    }

    try {
      isCreateMode ? await createDemand.mutateAsync(formattedData) : await updateDemands.mutateAsync(formattedData)
    } catch (error) {
      // Erro já tratado nas mutations
    }
  }

  const calculateTotalValue = (selectedProduct) => {
    const startTime = selectedProduct?.start_work ? selectedProduct.start_work : getValues('start_work')
    const endTime = selectedProduct?.end_work ? selectedProduct.end_work : getValues('end_work')

    const employees = selectedProduct?.employees || selectedEmployees
    if (!employees || employees.length === 0) return

    // Verificar se as horas estão preenchidas
    if (!startTime || !endTime) return

    const totalHourlyValue = calculateTotalHourlyValue(employees)
    const hoursWorked = calculateHoursWorked(startTime, endTime)

    setTotalValue(hoursWorked * totalHourlyValue)
    setHourlyValue(totalHourlyValue)

    reset({
      ...getValues(),
      hourly_value: totalHourlyValue,
      total_value: (hoursWorked * totalHourlyValue).toFixed(2),
    })
  }

  const calculateTotalHourlyValue = (employees) => {
    return employees
      .map((employee) => (employee ? Number(employee.hourly_value) : 0))
      .reduce((sum, value) => sum + value, 0)
  }

  const calculateHoursWorked = (startTime, endTime) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number)
    const [endHours, endMinutes] = endTime.split(':').map(Number)

    const start = new Date()
    start.setHours(startHours, startMinutes, 0)

    const end = new Date()
    end.setHours(endHours, endMinutes, 0)

    return Math.abs((end.getTime() - start.getTime()) / (1000 * 60 * 60))
  }

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" as="form" onSubmit={handleSubmit(handleCreateOrUpdate)}>
      <VStack spacing="8">
        <SimpleGrid minChildWidth="240px" spacing="8" width="100%">
          <Input
            label="Data"
            {...register('work_date')}
            error={errors.work_date as FieldError}
            as={InputMask}
            mask="99/99/9999"
          />
          <Input
            label="Hr. Início"
            {...register('start_work')}
            error={errors.start_work as FieldError}
            as={InputMask}
            mask="99:99:00"
            onChange={(e) => {
              setHasStartTime(!!e.target.value)
              if (hasEndTime && selectedEmployees.length > 0) {
                const endTime = getValues('end_work')
                calculateTotalValue({ 
                  ...selectedProduct, 
                  start_work: e.target.value, 
                  end_work: endTime,
                  employees: selectedEmployees 
                })
              }
            }}
          />
          <Input
            label="Hr. Final"
            {...register('end_work')}
            error={errors.end_work as FieldError}
            as={InputMask}
            mask="99:99:00"
            onChange={(e) => {
              setHasEndTime(!!e.target.value)
              if (hasStartTime && selectedEmployees.length > 0) {
                const startTime = getValues('start_work')
                calculateTotalValue({ 
                  ...selectedProduct, 
                  start_work: startTime,
                  end_work: e.target.value, 
                  employees: selectedEmployees 
                })
              }
            }}
          />

          <Input
            label="Valor Hora Total"
            {...register('hourly_value')}
            name="hourly_value"
            value={hourlyValue ? hourlyValue.toFixed(2) : ''}
            isReadOnly
            error={errors.hourly_value as FieldError}
          />

          <Input
            label="Valor Total"
            name="total_value"
            {...register('total_value')}
            value={totalValue ? totalValue.toFixed(2) : ''}
            onChange={(e) => setTotalValue(Number(e.target.value))}
            error={errors.total_value as FieldError}
          />

          {hasStartTime && hasEndTime && (
            <Box width="100%">
              <Text mb="4" mt="-2" fontWeight="bold" color="gray.300">
                Selecione os funcionários:
              </Text>

              <CheckboxGroup
                value={selectedEmployees.map((employee) => employee.id.toString())}
                onChange={(values) => {
                  const updatedEmployees = values
                    .map((value) => {
                      const employee = employees?.find((emp) => emp.id === Number(value))
                      return employee ? employee : null
                    })
                    .filter(Boolean) as WorkScheduleEmployee[]

                  setSelectedEmployees(updatedEmployees)

                  // Recalcular apenas se as horas estiverem preenchidas
                  if (hasStartTime && hasEndTime) {
                    const startTime = getValues('start_work')
                    const endTime = getValues('end_work')
                    calculateTotalValue({
                      ...selectedProduct,
                      employees: updatedEmployees,
                      start_work: startTime,
                      end_work: endTime,
                    })
                  }
                }}
              >
                {/* 🔹 Disposição dos nomes em 2 colunas com alinhamento perfeito */}
                <SimpleGrid columns={1} spacingY={2} spacingX={8}>
                  {employees?.map((employee) => (
                    <Checkbox
                      key={employee.id}
                      value={employee.id.toString()}
                      colorScheme="pink"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      <Text noOfLines={1} fontSize="sm" title={employee.name}>
                        {employee.name}
                      </Text>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </Box>
          )}
          {(!hasStartTime || !hasEndTime) && (
            <Box width="100%">
              <Text color="gray.500" fontSize="sm">
                Preencha as horas de início e fim para selecionar os funcionários
              </Text>
            </Box>
          )}
        </SimpleGrid>

        <Flex mt="8" justify="flex-end" width="100%">
          <HStack spacing="4">
            <Button 
              type="submit" 
              colorScheme="pink" 
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </Box>
  )
}
